/**
 * MultiPsk — Wireless > Multi-PSK (Identity PSK).
 *
 * Per-SSID pre-shared keys, each optionally MAC-bound and VLAN-mapped: per-room / per-device
 * keys on ONE WPA-PSK SSID, no captive portal or 802.1X. Reuses the Meridian system
 * (md-card, Button, Dialog) and the list+dialog pattern of Vouchers/GuestPortal.
 *
 * Passphrases are write-only: a generated/added key is shown ONCE (with copy); the list only
 * shows masked entries + MAC/VLAN. An SSID picker (PSK-capable networks) selects the target.
 */
import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import { useSsids, useSitePsks, useAddPsk, useRemovePsk } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

// PSK-capable securities (multi-PSK does not apply to open/enterprise SSIDs).
const PSK_SECURITIES = new Set(["wpa2", "wpa3", "wpa2/3"]);

export default function MultiPsk() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data: ssidData, isLoading: ssidsLoading } = useSsids(siteId);
  const [ssid, setSsid] = useState<string | null>(null);

  // PSK-capable SSIDs for the picker.
  const pskSsids = useMemo(
    () => (ssidData?.ssids ?? []).filter((s) => PSK_SECURITIES.has(s.security)),
    [ssidData],
  );
  // Default to the first PSK SSID once loaded.
  useEffect(() => {
    if (!ssid && pskSsids.length > 0) setSsid(pskSsids[0]!.name);
  }, [ssid, pskSsids]);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Multi-PSK" subtitle="Per-device / per-room keys (IPSK)" />
        <EmptyState
          title="Select a site first"
          hint="Multi-PSK keys apply to a site's WPA-PSK Wi-Fi. Pick a site from the scope bar above."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Multi-PSK" subtitle={siteName ? `Identity PSK in ${siteName}` : undefined} />

      {ssidsLoading && <div className="text-sm text-ink-muted">Loading SSIDs…</div>}

      {!ssidsLoading && pskSsids.length === 0 && (
        <EmptyState
          title="No WPA-PSK network"
          hint="Multi-PSK needs a WPA2/WPA3 personal SSID. Create one under Wireless → SSIDs first (open and enterprise SSIDs can't carry per-device keys)."
        />
      )}

      {pskSsids.length > 0 && (
        <>
          <label className="mb-4 flex max-w-xs flex-col gap-1 text-sm">
            <span className="text-ink-muted">SSID</span>
            <select
              value={ssid ?? ""}
              onChange={(e) => setSsid(e.target.value)}
              className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
            >
              {pskSsids.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name} · {s.security.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          {ssid && <PskPanel siteId={siteId} ssid={ssid} />}
        </>
      )}
    </div>
  );
}

function PskPanel({ siteId, ssid }: { siteId: string; ssid: string }) {
  const { data, isLoading, error } = useSitePsks(siteId, ssid);
  const [addOpen, setAddOpen] = useState(false);
  const psks = data?.psks ?? [];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm text-ink-muted">
          <span className="font-semibold text-ink">{psks.length}</span> key{psks.length === 1 ? "" : "s"} on{" "}
          <span className="font-mono text-ink">{ssid}</span>
        </div>
        <RoleGate min="site_operator" mode="hide">
          <Button variant="primary" onClick={() => setAddOpen(true)}>
            Add key
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading keys…</div>}

      {data && data.owgw_available === false && (
        <EmptyState title="No manageable APs" hint="No AP in this site has a live configuration." />
      )}
      {data && data.owgw_available && psks.length === 0 && (
        <EmptyState
          title="No per-device keys yet"
          hint="Add a key — leave the passphrase blank to auto-generate one. Bind it to a device MAC and/or a VLAN for per-identity segmentation."
        />
      )}

      {psks.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {psks.map((p) => (
            <li key={p.id} className="md-card flex items-center gap-3 p-2.5">
              <span className="font-mono text-sm tracking-widest text-ink-subtle">••••••••</span>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-xs">
                <span className="truncate font-medium text-ink">{p.label ?? "(unnamed)"}</span>
                <span className="font-mono text-ink-muted">{p.mac ?? "any device"}</span>
                {p.vlan_id != null ? (
                  <span className="rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-ink-muted">
                    VLAN {p.vlan_id}
                  </span>
                ) : (
                  <span className="text-[0.625rem] uppercase tracking-wide text-ink-subtle">no VLAN</span>
                )}
                {p.state !== "active" && (
                  <span className="text-[0.625rem] uppercase tracking-wide text-ink-subtle">{p.state}</span>
                )}
              </div>
              {p.state === "active" && (
                <RoleGate min="site_operator" mode="hide">
                  <RemovePskButton siteId={siteId} ssid={ssid} id={p.id} />
                </RoleGate>
              )}
            </li>
          ))}
        </ul>
      )}

      {addOpen && <AddPskDialog siteId={siteId} ssid={ssid} onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function RemovePskButton({ siteId, ssid, id }: { siteId: string; ssid: string; id: number }) {
  const remove = useRemovePsk(siteId, ssid);
  const [confirm, setConfirm] = useState(false);
  if (!confirm) {
    return (
      <Button variant="ghost" onClick={() => setConfirm(true)}>
        Remove
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(id)}>
        {remove.isPending ? "…" : "Confirm"}
      </Button>
      <Button variant="ghost" onClick={() => setConfirm(false)}>
        Cancel
      </Button>
    </div>
  );
}

function AddPskDialog({ siteId, ssid, onClose }: { siteId: string; ssid: string; onClose: () => void }) {
  const add = useAddPsk(siteId, ssid);
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [mac, setMac] = useState("");
  const [vlan, setVlan] = useState("");
  const issued = add.data?.key;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Blank key -> generate. If set, enforce the WPA passphrase length client-side.
    if (key && (key.length < 8 || key.length > 63)) return;
    add.mutate({
      label: label.trim() || undefined,
      key: key.trim() || undefined,
      mac: mac.trim() || undefined,
      vlan_id: vlan.trim() ? Number(vlan) : null,
    });
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Add key — {ssid}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            A per-device key on this SSID. Bind it to a MAC and/or VLAN for segmentation.
          </Dialog.Description>

          {!issued ? (
            <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Name (optional)</span>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Finance-Pass, Room 214, Printer…"
                  className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Passphrase (blank = auto-generate)</span>
                <input
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="8–63 characters, or leave blank"
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Device MAC (optional)</span>
                  <input
                    value={mac}
                    onChange={(e) => setMac(e.target.value)}
                    placeholder="aa:bb:cc:dd:ee:ff"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">VLAN (optional)</span>
                  <input
                    inputMode="numeric"
                    value={vlan}
                    onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="1–4094"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>
              </div>

              {add.isError && <span className="text-xs text-critical">Could not add the key.</span>}
              <div className="mt-1 flex items-center justify-end gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={add.isPending}>
                  {add.isPending ? "Adding…" : "Add key"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              <div className="rounded border border-brand/30 bg-brand/10 p-3">
                <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                  Passphrase — copy it now, it won't be shown again
                </div>
                <div className="mt-1 select-all break-all font-mono text-lg font-semibold tracking-wider text-ink">
                  {issued}
                </div>
              </div>
              <div className="text-xs text-ink-muted">
                {add.data?.mac ? `Bound to ${add.data.mac}` : "Any device"}
                {add.data?.vlan_id != null ? ` · VLAN ${add.data.vlan_id}` : ""}
                {add.data && add.data.push.pushed > 0 ? ` · pushed to ${add.data.push.pushed} AP${add.data.push.pushed === 1 ? "" : "s"}` : ""}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(issued)}>
                  Copy
                </Button>
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
